import Vue from 'vue';
import Vuex, { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import router from '@/router';
import i18n, { setIViewLocale } from '@/i18n';
//Store===========================================================
import { SigninModule } from '@/store/signin';
//Modal===========================================================
import { AuthInfo } from '@/models/auth-info';
import { Account } from '@/models/account';
import { Language } from '@/models/language';
//Service===========================================================
import { cookieService } from '@/service/cookie.service';
import { accountService } from '@/service/api/account.service';
import { storageService } from '@/service/storage/storage.service';
import { localStorageService } from '@/service/storage/local-storage.service';
import { authService } from '@/service/api/auth.service';
import { competencyBoxplotStorageService } from '@/service/storage/competency-boxplot-storage.service';
import { DashboardInfo } from '@/models/dashboard-info';
import { settingService } from '@/service/api/setting.service';
import { masterService } from '@/service/api/master.service';

Vue.use(Vuex);

export interface RootState {
  currentLanguage?: Language;
  authToken?: string; // After signin go API auth token string
  authInfo?: AuthInfo; // Signin Auth Info
  account?: Account;
  schoolYear: number;
  dashboardInfo: DashboardInfo;
  languages: Language[];
  // for vuex-router-sync.
  route: {
    path: string;
    params: { [key: string]: string };
    query: { [key: string]: string };
    fullPath: string;
  };
}

const stateOfModule: RootState = {
  authToken: undefined,
  authInfo: undefined,
  account: undefined,
  schoolYear: 0,
  currentLanguage: { langCode: 'ja', langName: '日本語', dispName: '日本語', nameOrder: true} as Language,
  dashboardInfo: undefined,
  languages: [],
  route: {
    path: '',
    params: {},
    query: {},
    fullPath: '',
  },
};

const gettersOfModule: GetterTree<RootState, RootState> = {
  isLogined(state: RootState): boolean {
    return state.authToken !== undefined && state.authToken !== '';
  },

  isLoggedIn(state: RootState): boolean {
    return state.authInfo !== undefined;
  },

  isHideHeader(state: RootState): boolean {
    return state.route.path === '/signin' || state.route.path.startsWith('/reset-password');
  },

  isNameOrder(state: RootState): boolean {
    return state.currentLanguage.nameOrder;
  },
};

const mutations: MutationTree<RootState> = {
  currentLanguage: (state: RootState, value: Language) => {
    state.currentLanguage = value;
  },
  languages: (state: RootState, value: Language[]) => {
    state.languages = value;
  },
  authToken: (state: RootState, value: string) => {
    state.authToken = value;
  },
  authInfo: (state: RootState, value: AuthInfo) => {
    state.authInfo = value;
  },
  account: (state: RootState, value: Account) => {
    state.account = value;
  },
  schoolYear: (state: RootState, value: number) => {
    state.schoolYear = value;
  },
  dashboardInfo: (state: RootState, value: DashboardInfo) => {
    state.dashboardInfo = value;
  },
};

const actions: ActionTree<RootState, RootState> = {
  async loadSettings(ctx: ActionContext<RootState, RootState>) {
    ctx.commit('authToken', localStorageService.authToken);
    // cookieService.language is string like "ja","en","vi"
    await ctx.dispatch("fetchLanguages");
    ctx.commit('currentLanguage', ctx.state.languages.find((l: Language) => {
      if (l.langCode === cookieService.language) return l;
    }));
    // i18n.locale = ctx.state.currentLanguage!.langCode;
    // Only Fetch the account info and tickect when user refresh the page
  },

  changeLanguage(ctx: ActionContext<RootState, RootState>, lang: Language) {
    cookieService.language = lang.langCode;
    i18n.locale = lang.langCode;
    // Don't forget to set locale for iView Component
    setIViewLocale();
    ctx.commit('currentLanguage', lang);
    window.location.reload();
  },

  setAuthToken(ctx: ActionContext<RootState, RootState>, authToken?: string) {
    if (authToken) {
      localStorageService.authToken = authToken;
    } else {
      localStorageService.removeAuthToken();
    }
    ctx.commit('authToken', authToken);
  },

  setSchoolYear(ctx: ActionContext<RootState, RootState>, schoolYear: number) {
    if (schoolYear > 0) {
      cookieService.schoolYear = String(schoolYear);
    } else {
      cookieService.removeSchoolYear();
    }
    ctx.commit('schoolYear', schoolYear);
  },

  setAuthInfo(ctx: ActionContext<RootState, RootState>, authInfo: AuthInfo) {
    ctx.commit('authToken', authInfo.authToken, { root: true });
    ctx.commit('authInfo', authInfo, { root: true });
  },

  signout(ctx: ActionContext<RootState, RootState>) {
    authService.signout().then(() => {
      ctx.commit('authToken', undefined);
      ctx.commit('authInfo', undefined);
      cookieService.deleteAll();
      localStorageService.removeAuthToken();
      storageService.removeAdminAuthToken();
      storageService.removeOauthItems();
      competencyBoxplotStorageService.deleteItem(ctx.state.schoolYear);
      router.replace({ path: '/signin' });
    });
  },

  async getDashboardInfo(ctx: ActionContext<RootState, RootState>) {
    const dashboardInfo: DashboardInfo = await settingService.getDashboardInfo();
    ctx.commit('dashboardInfo', dashboardInfo);
  },

  async fetchAccount(ctx: ActionContext<RootState, RootState>) {
    const account: Account = await accountService.getAccountProfile();
    ctx.commit('account', account);
  },

  async fetchLanguages(ctx: ActionContext<RootState, RootState>) {
    const languages: Language[] = await masterService.fetchLanguages();
    ctx.commit('languages', languages);
  },

  handleBackToHome(_: ActionContext<RootState, RootState>) {
    router.push('/pages/home');
  },

  invalidateAuthToken(ctx: ActionContext<RootState, RootState>) {
    localStorageService.removeAuthToken();
    ctx.commit('authToken', undefined);
  },
};

export const store = new Vuex.Store<RootState>({
  state: stateOfModule,
  getters: gettersOfModule,
  mutations,
  actions,
  modules: {
    signin: SigninModule,
  },
});
